import { CPLAT_API_ROUTES, sellerQueries, usePost } from '@/api';
import DeleteButtonIcon from '@/assets/icons/DeleteButtonIcon.svg?react';
import GreenCheckIcon from '@/assets/icons/GreenCheckIcon.svg?react';
import { useToast } from '@/hooks/V3/useToast';
import { useAppSelector } from '@/redux/store';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronRightIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import Input from '../V3/Input';
import { Dialog, DialogClose, DialogContent, DialogTrigger } from '../atoms/Dialog';
import { Button } from '../atoms/NewPcButton';

const formSchema = z.object({
  email: z.email(),
});

type FormType = z.infer<typeof formSchema>;

interface Props {
  defaultValue?: string;
}

export const EmailDialog = ({ defaultValue }: Props) => {
  const user = useAppSelector((state) => state.user);
  const cplatToken = user.CplatToken;

  const queryClient = useQueryClient();

  const { showToast } = useToast();

  const [open, setOpen] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  const {
    register,
    formState: { isValid, errors },
    watch,
    setError,
    setValue,
    clearErrors,
  } = useForm<FormType>({ resolver: zodResolver(formSchema), shouldUnregister: true });

  const watchedEmail = watch('email');

  const { mutate: onVerifyEmail } = usePost(
    CPLAT_API_ROUTES.verifyEmail,
    undefined,
    undefined,
    true,
  );

  const { mutate: onUpdateInfo } = usePost(CPLAT_API_ROUTES.updateInfo, undefined, undefined, true);

  const handleVerifyEmail = () => {
    onVerifyEmail(
      { accountEmail: watchedEmail },
      {
        onSuccess: (response) => {
          const isRegistered = response.data.data[0].isRegistered;
          if (isRegistered) setError('email', { message: '이미 사용중인 이메일입니다.' });
          else setEmailVerified(true);
        },
      },
    );
  };

  const handleUpdateInfo = () => {
    onUpdateInfo(
      { cplatToken, accountEmail: watchedEmail },
      {
        onSuccess: () => {
          setOpen(false);
          queryClient.invalidateQueries({ queryKey: sellerQueries.all() });
          showToast('success', '이메일 주소 변경이 완료되었어요');
        },
        onError: () => showToast('warning', '일시적 오류로 변경을 실패했어요'),
      },
    );
  };

  useEffect(() => {
    clearErrors();
  }, [watchedEmail]);

  useEffect(() => {
    setEmailVerified(false);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger className="flex w-full items-center justify-between text-gr-700 Body2M16">
        <span className="text-gr-700 Body2M16">이메일</span>
        <div className="flex items-center ">
          {defaultValue}
          <ChevronRightIcon className="h-5 w-5 text-gr-400" />
        </div>
      </DialogTrigger>
      <DialogContent className="w-[400px] gap-5" noCloseBtn>
        <div className="space-y-1">
          <div className="text-gr-900 Title3S18">이메일 변경</div>
          <div className="text-gr-700 Body5M14">아이디로 사용될 새 이메일 주소를 입력해 주세요</div>
        </div>
        <div className="flex  gap-2">
          <Input
            disabled={emailVerified}
            placeholder="이메일 주소를 입력해 주세요"
            {...register('email')}
            InputProps={{
              endAdornment: emailVerified ? (
                <GreenCheckIcon />
              ) : (
                watchedEmail && (
                  <button onClick={() => setValue('email', '')}>
                    <DeleteButtonIcon />
                  </button>
                )
              ),
            }}
            Error={{ isError: !!errors.email?.message, errorMessage: errors.email?.message }}
          />
          <Button variant="line" disabled={!isValid || emailVerified} onClick={handleVerifyEmail}>
            중복확인
          </Button>
        </div>
        <div className="flex gap-3">
          <DialogClose asChild>
            <Button variant="secondary" className="flex-1">
              취소
            </Button>
          </DialogClose>
          <Button className="flex-1" disabled={!emailVerified} onClick={handleUpdateInfo}>
            변경
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
